import React, { useState, useEffect, useRef } from 'react';
import { getTimeDifferenceString } from 'utils/Utils';
import { Box, Button, Icon, Card, CardContent, CircularProgress, SvgIcon, Dialog, FormControl, InputLabel, Stack, Grid, Typography, List, ListItem, ListItemButton, ListItemText, Popover, useMediaQuery, DialogTitle, DialogContent, DialogContentText, DialogActions, Link, Drawer } from '@mui/material';
const backIcon = `${process.env.REACT_APP_HOMEPAGE}assets/arrow_back.svg`;
import MyResumeDetails from './MyResumeDetails';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { getResumesData, updateResumeUrl, addCandidateResume, updateMainResume } from 'api/users';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'components/snackbar';
import { SplashScreen } from 'components/loading-screen';
import { useTheme } from '@mui/material/styles';
import LeaveWithoutCreate from './LeaveWithoutCreate';
import { downloadBlobFile } from 'utils/Utils';
import UploadResumeNavigation from './UploadResumeNavigation';
import { useBackNavigationDialog } from 'hooks/useBackNavigationDialog';

const MyResumes = ({ jobData, tabForRedirectBack, redirectToExternalJob, resumeId }) => {
  const [selectedCard, setSelectedCard] = useState('');
  const fileInputRef = useRef(null);
  const [resumeData, setResumeData] = useState([]);
  // const [openDetailsDrawer, setOpenDetailsDrawer] = React.useState(false);
  const [selectedResume, setSelectedResume] = useState(null);
  // const [showOverlayDialog, setShowOverlayDialog] = useState(false);
  // const [openUploadResumeDialog, setOpenUploadResumeDialog] = useState(false);
  const [resumeLink, setResumeLink] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { openDialog, closeDialog, dialogLocation } = useBackNavigationDialog();
  const dialogIds = dialogLocation.state?.dialogIds || [];

  const openDetailsDrawer = dialogIds.includes('detailsDrawer');
  const showOverlayDialog = dialogIds.includes('overlayDialog');
  const openUploadResumeDialog = dialogIds.includes('uploadResumeDialog');

  /**
   * Fetches all resumes of the user from the server and stores them in the
   * resumeData state. It also selects the first resume with a link and active
   * status as the selected resume and sets it in the selectedCard state.
   *
   * @function
   * @returns {Promise<Array>} - Resolves with the fetched resume data.
   */
  const fetchData = async () => {
    try {
      const response = await getResumesData();

      if (response?.success && response?.data && response.data?.length > 0) {
        const resumes = response.data;
        const selectedResume = resumes.find(item => item.link && item.status === 'active') || resumes[0];

        setSelectedCard(selectedResume._id);
        setSelectedResume(selectedResume);

        const sortedResumes = [selectedResume, ...resumes.filter(item => item._id !== selectedResume._id)];
        setResumeData(sortedResumes);

        return sortedResumes;
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      enqueueSnackbar('Failed to fetch data. Please try again later.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    /**
     * Fetches all resumes of the user from the server and sets the selected card and resume
     * to the resume with the given resumeId if it exists. If the resumeId is not provided or
     * the resume does not exist, it does nothing.
     *
     * @function
     * @returns {Promise<void>}
     */
    const fetchDataAsync = async () => {
      const allResumes = await fetchData();
      if (!allResumes || !resumeId) return;

      const matchedResume = allResumes.find(item => item._id === resumeId);
      if (matchedResume) {
        setSelectedCard(matchedResume._id);
        setSelectedResume(matchedResume);
      }
    };

    fetchDataAsync();
  }, []);
  // useEffect(() => {
  //   if (isMobile) {
  //     if (openDetailsDrawer) {
  //       localStorage.setItem('tab', '4');
  //     } else {
  //       localStorage.removeItem('tab');
  //     }
  //   }
  // }, [openDetailsDrawer]);

  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [isActive, setActive] = React.useState(false);

  /**
   * Handles the click event on a specific element. Sets the anchor element
   * to the event's current target and activates the related UI component.
   *
   * @param {Object} event - The click event object.
   */

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setActive(true);
  };

  /**
   * Handles the click event on the "Create a Resume" button in the 
   * "My Resumes" component. If the user does not have a master resume, 
   * navigates to the create-update-resume page with state { isMaster: true }.
   * Otherwise, navigates to the /profile/resume page with the master resume
   * details.
   */
  const handleClicked = async () => {
    const isResume = resumeData?.find(item => item.title === 'Master Resume');
    if (!isResume) {
      navigate('/create-update-resume', { state: { isMaster: true, jobData, isExternalJob: true } });
    } else handleNavigation();
  }
  /**
   * Closes the open popover by resetting the anchor element to null and 
   * setting the active state to false, effectively deactivating the related 
   * UI component.
   */

  const handleClose = () => {
    setAnchorEl(null);
    setActive(false);
  };
  /**
   * Cancels the upload resume process by closing the overlay dialog and the
   * upload resume dialog.
   */
  const handleCancel = () => {
    console.log("HANDLE CANCEL");
    // setShowOverlayDialog(false);
    // setOpenUploadResumeDialog(false);
    closeDialog('overlayDialog');
    closeDialog('uploadResumeDialog');
  }
  /**
   * Proceeds with uploading the resume.
   * Finds the active resume with a link in the resume data array and navigates to the
   * create-update-resume page with the found resume data, total number of resumes, and
   * a flag indicating that the user wants to upload a new resume.
   */
  const handleProceed = () => {
    const isResume = resumeData?.find(item => item.link && item.status === 'active');
    navigate('/create-update-resume', { state: { data: isResume, totalResume: resumeData?.length, uploadResume: true, jobData, isExternalJob: true } });
  }
  const [withoutUpdateDialog, setLeaveWihoutUpdatingDialog] = React.useState(false);
  const [isFileSelected, setIsFileSelected] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewLoader, setPreviewLoader] = useState(false);
  /**
   * Opens the LeaveWithoutUpdatingDialog if there is no master resume, or navigates
   * to the create-update-resume page with state { isMaster: true } if a master resume
   * exists. Otherwise, calls the handleClicked function to navigate to the
   * /profile/resume page with the master resume details.
   */
  const handleOpenLeaveWihoutUpdatingDialog = () => {
    const isResume = resumeData?.find(item => item.title === 'Master Resume');
    if (!isResume) {
      navigate('/create-update-resume', { state: { isMaster: true, jobData, isExternalJob: true } });
    } else {
      handleClicked();
    }
  };
  /**
   * Closes the LeaveWithoutUpdatingDialog by setting the
   * withoutUpdateDialog state to false.
   */
  const handleCloseLeaveWihoutUpdatingDialog = () => {
    setLeaveWihoutUpdatingDialog(false);
  };
  const openCS = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  /**
   * Toggles the details drawer open or closed based on the newOpen parameter.
   * @param {boolean} newOpen - Whether to open or close the details drawer.
   */
  const handleOpenDrawer = (newOpen) => () => {
    // setOpenDetailsDrawer(newOpen);
    if (newOpen) {
      handleOpenMobileDrawer();
    } else {
      handleCloseMobileDrawer();
    }
  };

  const handleOpenMobileDrawer = () => openDialog('detailsDrawer');
  const handleCloseMobileDrawer = () => {
    const timer = setTimeout(() => {
      closeDialog('detailsDrawer');
    }, 300);
  }
  /**
   * Handles the click event on a resume card in the resume list.
   * @param {Object} resume - The resume object that was clicked.
   * @description
   * - Sets the selected card ID to the resume's ID.
   * - Sets the selected resume to the resume object.
   * - Opens the details drawer.
   */
  const handleCardClick = (resume) => {
    setSelectedCard(resume._id);
    setSelectedResume(resume);
    // setOpenDetailsDrawer(true);
    handleOpenMobileDrawer();
  };
  /**
   * Handles the change in resume data by fetching updated data.
   * Asynchronously calls the fetchData function to refresh the
   * resume list with the latest information.
   */

  const handleResumeDataChange = async () => {
    await fetchData();
  };

  const bankInIcon = (
    <Icon className="extLoginIcon">
      <img alt="edit" width="24px" height="24px" src={backIcon} />
    </Icon>
  );

  /**
   * Navigates to the create-update-resume page with the total number of resumes and
   * a flag indicating that the user wants to create a new resume.
   */
  const handleNavigation = () => {
    navigate('/create-update-resume', { state: { totalResume: resumeData.length, createNewResume: true, jobData, isExternalJob: true } });
  };

  /**
   * Triggers the file input click event to open the file selector dialog.
   * This allows the user to select a resume file to upload.
   */
  const handleUploadResume = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }
  /**
   * Redirects to the external job application page based on the provided application data and tab context.
   * @param {Object} applicationData - The application data object containing the job ID and other relevant information.
   * @param {string} tab - The current tab identifier in the UI.
   * @description
   * - Calls the redirectToExternalJob function with the application data and tab context.
   * - Only redirects if both the application data and tab context are present.
   */
  const handleRedirectToExternalJob = (applicationData, tab, flag) => {
    if (applicationData && tab) {
      redirectToExternalJob(applicationData, tab, flag);
    }
  };

  if (loading) {
    return (
      <Grid container justifyContent="center" sx={{ position: 'relative', overflow: 'hidden', width: '100%', height: 'calc(100vh - 10vh)' }} alignItems="center">
        <SplashScreen />
      </Grid>
    );
  }

  /**
   * Handles file change event for the file input element.
   * Checks if the selected file is valid (not empty, less than 2MB, and of allowed type).
   * If the file is valid, it uploads the file to the server and updates the local resume data.
   * If the upload fails, it logs an error to the console and displays an error message to the user.
   * Finally, it opens the overlay dialog to display the updated resume data.
   * @param {Event} event - The file input change event.
   */
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      enqueueSnackbar("No file selected. Please choose a file to upload", { variant: "warning" });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      enqueueSnackbar("File size must be less than 2 MB. Please upload a smaller file", { variant: "error" });
      return;
    }
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ];

    if (!allowedTypes.includes(file.type)) {
      enqueueSnackbar("Invalid file type. Only PDF, DOC, and DOCX files are allowed", { variant: "error" });
      return;
    }

    setPreviewLoader(true);
    setSelectedFile(file);
    setIsFileSelected(true);
    setIsLoading(true);
    // setShowOverlayDialog(false);
    closeDialog('overlayDialog');
    try {
      await handleFileSubmit(file);


      // setShowOverlayDialog(true);
      openDialog('overlayDialog');
    } catch (error) {
      enqueueSnackbar("Upload failed!", { variant: "error" });
      console.error("Upload error:", error);
    } finally {
      setPreviewLoader(false);
      // setShowOverlayDialog(true);
      // openDialog('overlayDialog');
      setIsLoading(false);
    }
  };

  /**
   * Handles the file submission event by uploading the selected file to the server.
   * @param {File} selectedFile - The file selected by the user to upload.
   * @description
   * - Validates the selected file to ensure it is not empty and is of allowed type.
   * - If the file is valid, it uploads the file to the server using the addCandidateResume API.
   * - If the upload is successful, it updates the local resume data and displays a success message to the user.
   * - If the upload fails, it displays an error message to the user.
   * - Finally, it sets the isFileSelected state to false to reset the file input element.
   */
  const handleFileSubmit = async (selectedFile) => {
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const response = await addCandidateResume(formData);
      if (response?.success) {
        const masterResume = await updateMainResume({ title: 'Upload Resume' });
        enqueueSnackbar("Resume uploaded successfully", { variant: "success" });
        console.log("Upload Success:", response);
        setResumeLink(`${process.env.REACT_APP_END_POINT}${response?.data?.resumeUrl}`)
        // setOpenUploadResumeDialog(true);
        closeDialog('uploadResumeDialog');
        openDialog('overlayDialog');
        // setShowOverlayDialog(true);
        if (masterResume) {
          handleResumeDataChange()
          console.log("Status Updated");
        } else {
          console.log("ERROR in updating status");

        }
      } else {
        enqueueSnackbar("Failed to upload resume. Please try again.", { variant: "error" });
      }
    } catch (error) {
      console.error("Upload Error:", error);
      enqueueSnackbar("Failed to upload resume. Please try again.", { variant: "error" });
    } finally {
      setIsFileSelected(false);

    }
  }

  const tjResumeCount = resumeData.filter(resume => !resume?.link).length;
  const uploadedResumeCount = resumeData.filter(resume => resume?.link).length;

  return (
    <Box sx={{
      position: 'relative',
      overflow: 'hidden',
      width: '100%',
      height: 'calc(100vh - 100px)'
    }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4} >
          <Box className="resume-type-container">
            <Box className="resume-type-header" >
              <Typography className="title">My Resumes</Typography>
            </Box>
            <Box className="resume-type-content" sx={{ textAlign: 'center' }}>

              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  height: 'auto'
                }}>
                {resumeData?.map((resume) => (
                  <Card
                    className={`resume-type-card master-resume-card ${selectedCard === resume._id ? ' selected-card' : ''
                      }`}
                    variant="outlined"
                    onClick={() => handleCardClick(resume)}
                    sx={{ marginBottom: '1rem' }}
                    key={resume?._id || index}
                  >
                    <CardContent>
                      {resume.IsMostRelevent && (
                        <Typography variant="p" className="suggestion-tag">
                          Most Relevant For This Job
                        </Typography>
                      )}
                      <Box>
                        <Stack direction="row" alignItems={'center'} justifyContent={'space-between'}>
                          <Typography className="card-title">{resume?.link ? 'Uploaded Resume' : resume?.title}</Typography>
                          <SvgIcon sx={{ display: { xs: 'block', md: 'none', lg: 'none' } }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                              <path d="M7.45837 3.39961L12.8917 8.83294C13.5334 9.47461 13.5334 10.5246 12.8917 11.1663L7.45837 16.5996" stroke="#6B7280" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                          </SvgIcon>
                        </Stack>
                        <Typography className="last-updated">
                          {
                            (resume?.last_updated_at || resume?.updatedAt)
                              ? getTimeDifferenceString(resume.last_updated_at || resume.updatedAt, "Updated")
                              : 'N/A'
                          }
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}

                {uploadedResumeCount === 0 && tjResumeCount < 4 ? (
                  <Box className="add-resume-dp-container">
                    <Button mb={2} className={isActive ? 'active add-resume-dropdown-btn' : 'add-resume-dropdown-btn'} variant="text" endIcon={
                      <SvgIcon sx={{ width: '20px', height: '20px' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M19.9181 8.94995L13.3981 15.47C12.6281 16.24 11.3681 16.24 10.5981 15.47L4.07812 8.94995" stroke={resumeData?.length >= 5 ? "lightgray" : "#1575C6"} stroke-width="1.8" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                      </SvgIcon>
                    } aria-describedby={id} onClick={handleClick} disableRipple disableElevation disabled={resumeData?.length >= 5}>Add New Resume</Button>

                    <Popover
                      id={id}
                      className="add-resume-dropdown-popover"
                      open={openCS}
                      anchorEl={anchorEl}
                      onClose={handleClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                      }}
                    >
                      <Box className="custom-sec-list">
                        <List >
                          <ListItem disablePadding>
                            <ListItemButton onClick={() => { handleOpenLeaveWihoutUpdatingDialog(); setAnchorEl(null); handleClose() }} disabled={tjResumeCount === 4} >
                              <ListItemText sx={{ margin: 0 }} primary="Create TJ resume" className="create-new-resume-btn" />
                            </ListItemButton>
                          </ListItem>
                          <ListItem disablePadding>
                            <ListItemButton onClick={() => { handleUploadResume(); handleClose(); }}>
                              <ListItemText sx={{ margin: 0 }} primary="Upload existing resume" />
                            </ListItemButton>
                          </ListItem>
                        </List>
                      </Box>
                    </Popover>
                  </Box>
                ) : (
                  uploadedResumeCount === 0 ? (
                    <Button
                      variant="outlined"
                      size="large"
                      disabled={uploadedResumeCount !== 0}
                      sx={{ border: 'none!important' }}
                      className="create-new-resume-btn"
                      onClick={() => {
                        handleUploadResume();
                        handleClose();
                      }}
                    >
                      + Upload existing resume
                    </Button>

                  ) : (
                    <Button variant="outlined" size="large" disabled={tjResumeCount === 4} sx={{ border: 'none!important' }} className="create-new-resume-btn" onClick={() => { handleOpenLeaveWihoutUpdatingDialog(); setAnchorEl(null); }} >
                      + Create TJ Resume
                    </Button>
                  )
                )}
                <Typography sx={{ marginTop: '1rem', px: '20px' }} className="total-resume-text">
                  A maximum of 5 resumes can be added - 4 in Talent Junction style and 1 uploaded version.
                </Typography>
                {tjResumeCount === 4 ? <Typography sx={{ marginTop: '1rem', px: '20px' }} className="total-resume-text-alert">
                  Please delete one of the Talent Junction style resumes to create a new one.
                </Typography> : null}
              </Box>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={8}>
          <Grid item xs={12} md={12} sx={{ display: { xs: 'none', md: 'block' } }}>
            {resumeData && <MyResumeDetails
              tjResumeCount={tjResumeCount}
              setPreviewLoader={setPreviewLoader}
              data={selectedResume}
              totalResume={resumeData?.length}
              onDataChange={handleResumeDataChange}
              jobData={jobData}
              redirectedToExternalJob={handleRedirectToExternalJob}
            />}
          </Grid>
        </Grid>
      </Grid >
      {
        isMobile &&
        <Drawer className="mob-side-panel" open={openDetailsDrawer} anchor={'right'} onClose={handleOpenDrawer(false)}>
          <MyResumeDetails tjResumeCount={tjResumeCount} setPreviewLoader={setPreviewLoader} data={selectedResume} totalResume={resumeData?.length} onClose={() => handleCloseMobileDrawer()} onDataChange={handleResumeDataChange} jobData={jobData} redirectedToExternalJob={handleRedirectToExternalJob} />
        </Drawer>
      }
      {isMobile ?
        <Drawer
          anchor="bottom"
          open={withoutUpdateDialog}
          onClose={handleCloseLeaveWihoutUpdatingDialog}
          className="forgot-password-dialog career-breaks-dialog"
        >
          <LeaveWithoutCreate onClose={handleCloseLeaveWihoutUpdatingDialog} handleClicked={handleClicked} />
        </Drawer> :
        <Dialog
          open={withoutUpdateDialog}
          onClose={handleCloseLeaveWihoutUpdatingDialog}
          className="action-dialog-card"
        >
          <LeaveWithoutCreate onClose={handleCloseLeaveWihoutUpdatingDialog} handleClicked={handleClicked} />
        </Dialog>}

      {isMobile ?
        <Drawer open={openUploadResumeDialog} anchor={'right'}
          fullScreen
          className='preview-resume-dialog'
          sx={{ "& .MuiDrawer-paper": { width: "100%" } }}
        >
          <Box sx={{ width: { xs: '100%', md: '450px' } }} className="job-app-drawer-container" role="presentation" >
            <Box className="drawer-header">
              <Stack direction="row" alignItems={'flex-start'} spacing={1}>
                <Box>
                  <Typography className="title">Uploaded Resume</Typography>
                </Box>
              </Stack>
            </Box>
            <Box className="drawer-content">
              <Box className="my-resume-overview" >
                {isLoading ? (
                  <Stack justifyContent="center" alignItems="center" >
                    <CircularProgress size={40} />
                  </Stack>
                ) : resumeLink ? (
                  <>
                    <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js" />
                    <Viewer fileUrl={resumeLink} width={window.innerWidth * 1} />
                  </>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                      width: "100%",
                      border: "2px dashed #ccc",
                      borderRadius: "8px",
                      cursor: "pointer",
                      textAlign: "center",
                      padding: "20px"
                    }}
                    onClick={() => fileInputRef.current.click()}
                  >
                    <Typography variant="body1" color="textSecondary">No resume uploaded</Typography>
                    <Button
                      variant="contained"
                      sx={{ mt: 2, backgroundColor: "#1575C6", "&:hover": { backgroundColor: "#0056b3" } }}
                    >
                      Please upload in PDF (preferred) or word format
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
            <Box className="drawer-footer">
              <Stack direction="row" spacing={2}>
                <Button fullWidth size="large" className="cancel-btn"
                  onClick={() => fileInputRef.current.click()}
                  disabled={isLoading}
                >
                  Replace
                </Button>
                <Button fullWidth variant="contained" size="large" className="update-btn"
                  onClick={() => navigate('/registration-upload-resume')}
                  disabled
                >
                  Continue
                </Button>
              </Stack>
            </Box>
          </Box>
        </Drawer>
        :
        <Dialog open={openUploadResumeDialog} onClose={() => closeDialog('uploadResumeDialog')} sx={{ maxWidth: "600px", margin: "auto", zIndex: 1300 }} fullWidth>
          <DialogTitle sx={{ padding: "12px 20px", borderBottom: "1px solid #ddd" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Uploaded Resume</Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" size="small" className="update-btn"
                  onClick={() => fileInputRef.current.click()}
                  disabled={isLoading}
                  sx={{ color: "#1575C6", textTransform: "none", fontWeight: 500 }}
                >
                  Replace
                </Button>
                <Button variant="contained" size="small" className="select-resume-btn"
                  onClick={() => navigate('/registration-upload-resume')}
                  disabled
                  sx={{
                    backgroundColor: "#1575C6",
                    color: "white",
                    textTransform: "none",
                    fontWeight: 500,
                    "&:hover": { backgroundColor: "#0056b3" },
                  }}
                >
                  Continue
                </Button>
              </Stack>
            </Stack>
          </DialogTitle>

          {/* PDF Viewer */}
          <DialogContent sx={{ padding: "10px", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Box
              className="resume-options-preview"
              sx={{
                height: "75vh",
                width: "100%",
                overflow: "hidden",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: "8px",
              }}
            >
              {isLoading ? (
                <Stack justifyContent="center" alignItems="center">
                  <CircularProgress size={40} />
                </Stack>
              ) : resumeLink ? (
                <>
                  <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js" />
                  <Box sx={{ width: "100%", height: "100%" }}>
                    <Viewer fileUrl={resumeLink} />
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    width: "100%",
                    border: "2px dashed #ccc",
                    borderRadius: "8px",
                    cursor: "pointer",
                    textAlign: "center",
                    padding: "20px"
                  }}
                  onClick={() => fileInputRef.current.click()}
                >
                  <Typography variant="body1" color="textSecondary">No resume uploaded</Typography>
                  <Button
                    variant="contained"
                    sx={{ mt: 2, backgroundColor: "#1575C6", "&:hover": { backgroundColor: "#0056b3" } }}
                  >
                    Please upload in PDF (preferred) or word format
                  </Button>
                </Box>
              )}

            </Box>

          </DialogContent>
        </Dialog>
      }
      {isMobile ?
        <Drawer
          anchor="bottom"
          open={showOverlayDialog}
          className="forgot-password-dialog career-breaks-dialog"
        >
          <UploadResumeNavigation handleProceed={handleProceed} handleCancel={handleCancel} />
        </Drawer> :
        <Dialog
          open={showOverlayDialog}
          className="action-dialog-card"
        >
          <UploadResumeNavigation handleProceed={handleProceed} handleCancel={handleCancel} />
        </Dialog>}
      <input type="file"
        accept=".pdf, .doc, .docx, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* blur screen  */}
      <Drawer open={previewLoader}>
        <div className="loading-overlay">
          <div className="loading-spinner">
            <CircularProgress />
          </div>
        </div>
      </Drawer>
    </Box >
  );
};

export default MyResumes;
